import { spring, TransitionMotion } from '@serprex/react-motion';
import React from 'react';
import { connect, DispatchProp } from 'react-redux';

import {
    ThemeStyles,
    DarkAbilitiesSvg,
    DarkActionsSvg,
    DarkSkillsSvg,
    DarkEquipmentSvg,
    DarkSpellsSvg,
    DarkProficienciesSvg,
    DarkDescriptionSvg,
    DarkNotesSvg,
    DarkFeaturesSvg,
    DarkExtrasSvg,
    LightAbilitiesSvg,
    LightActionsSvg,
    LightSkillsSvg,
    LightEquipmentSvg,
    LightSpellsSvg,
    LightProficienciesSvg,
    LightDescriptionSvg,
    LightNotesSvg,
    LightFeaturesSvg,
    LightExtrasSvg,
} from '@dndbeyond/character-components/es';
import { rulesEngineSelectors, DecorationInfo, DecorationUtils } from '@dndbeyond/character-rules-engine/es';

import { sidebarActions } from '../../../Shared/actions/sidebar';
import { Sidebar } from '../../../Shared/containers/Sidebar';
import { appEnvSelectors } from '../../../Shared/selectors';
import { AppEnvDimensionsState } from '../../../Shared/stores/typings';
import { ComponentCarousel, ComponentCarouselItem } from '../../components/ComponentCarousel';
import { QuickNav, QuickNavItem } from '../../components/QuickNav';
import SectionPlaceholder from '../../components/SectionPlaceholder';
import { sheetAppSelectors } from '../../selectors';
import { SheetAppState, SidebarInfo, SidebarPositioningInfo } from '../../typings';
import CharacterHeaderMobile from '../CharacterHeaderMobile';
import ActionsMobile from '../mobile/ActionsMobile';
import CombatMobile from '../mobile/CombatMobile';
import DescriptionMobile from '../mobile/DescriptionMobile';
import EquipmentMobile from '../mobile/EquipmentMobile';
import ExtrasMobile from '../mobile/ExtrasMobile';
import FeaturesMobile from '../mobile/FeaturesMobile';
import MainMobile from '../mobile/MainMobile';
import NotesMobile from '../mobile/NotesMobile';
import ProficiencyGroupsMobile from '../mobile/ProficiencyGroupsMobile';
import SkillsMobile from '../mobile/SkillsMobile';
import SpellsMobile from '../mobile/SpellsMobile';

const SECTION = {
    __FILLER__: '__FILLER__',
    ACTIONS: 'ACTIONS',
    SKILLS: 'SKILLS',
    MAIN: 'MAIN',
    PROFICIENCIES_LANGUAGES: 'PROFICIENCIES_LANGUAGES',
    FEATURES_TRAITS: 'FEATURES_TRAITS',
    EQUIPMENT: 'EQUIPMENT',
    SPELLS: 'SPELLS',
    NOTES: 'NOTES',
    HIT_POINTS: 'HIT_POINTS',
    DESCRIPTION: 'DESCRIPTION',
    EXTRAS: 'EXTRAS',
};

interface Props extends DispatchProp {
    sidebarPositioning: SidebarPositioningInfo;
    sidebarInfo: SidebarInfo;
    envDimensions: AppEnvDimensionsState;
    decorationInfo: DecorationInfo;
    hasSpells: boolean;
    isReadonly: boolean;
}
interface State {
    activeSectionId: string;
    swipedAmount: number;
}
class CharacterSheetMobile extends React.PureComponent<Props, State> {
    constructor(props) {
        super(props);

        this.state = {
            activeSectionId: SECTION.MAIN,
            swipedAmount: 0,
        };
    }

    handleComponentChange = (id: string): void => {
        this.setState({
            activeSectionId: id,
        });
        window.scrollTo(0, 0);
    };

    handleSidebarClick = (): void => {
        const { dispatch, sidebarInfo } = this.props;

        dispatch(sidebarActions.visibleSet(!sidebarInfo.isVisible));
    };

    handleItemChange = (newKey: string, oldKey: string): void => {
        if (newKey !== oldKey) {
            window.scrollTo(0, 0);
        }

        this.setState({
            activeSectionId: newKey,
        });
    };

    getPositionX = (swipedAmount: number): number => {
        const { sidebarInfo } = this.props;

        let position: number = (sidebarInfo.isVisible ? 0 : sidebarInfo.width) + swipedAmount;

        if (swipedAmount > 0) {
            return Math.min(sidebarInfo.width, position);
        } else {
            return Math.max(0, position);
        }
    };

    renderSections = (): React.ReactNode => {
        const { activeSectionId } = this.state;
        const { envDimensions, hasSpells, isReadonly, decorationInfo } = this.props;

        const isDarkMode = DecorationUtils.isDarkMode(decorationInfo);

        return (
            <ComponentCarousel
                activeItemKey={activeSectionId}
                onItemChange={this.handleItemChange}
                envDimensions={envDimensions}
            >
                <ComponentCarouselItem
                    itemKey={SECTION.MAIN}
                    PlaceholderComponent={SectionPlaceholder}
                    placeholderProps={{
                        name: 'Abilities, Saves, Senses',
                        IconComponent: isDarkMode ? LightAbilitiesSvg : DarkAbilitiesSvg,
                    }}
                    ContentComponent={MainMobile}
                />
                <ComponentCarouselItem
                    itemKey={SECTION.SKILLS}
                    PlaceholderComponent={SectionPlaceholder}
                    placeholderProps={{
                        name: 'Skills',
                        IconComponent: isDarkMode ? LightSkillsSvg : DarkSkillsSvg,
                    }}
                    ContentComponent={SkillsMobile}
                />
                <ComponentCarouselItem
                    itemKey={SECTION.ACTIONS}
                    PlaceholderComponent={SectionPlaceholder}
                    placeholderProps={{
                        name: 'Actions',
                        IconComponent: isDarkMode ? LightActionsSvg : DarkActionsSvg,
                    }}
                    ContentComponent={ActionsMobile}
                />
                <ComponentCarouselItem
                    itemKey={SECTION.EQUIPMENT}
                    PlaceholderComponent={SectionPlaceholder}
                    placeholderProps={{
                        name: 'Inventory',
                        IconComponent: isDarkMode ? LightEquipmentSvg : DarkEquipmentSvg,
                    }}
                    ContentComponent={EquipmentMobile}
                />
                <ComponentCarouselItem
                    itemKey={SECTION.SPELLS}
                    PlaceholderComponent={SectionPlaceholder}
                    placeholderProps={{
                        name: 'Spells',
                        IconComponent: isDarkMode ? LightSpellsSvg : DarkSpellsSvg,
                    }}
                    ContentComponent={SpellsMobile}
                    isEnabled={hasSpells}
                />
                <ComponentCarouselItem
                    itemKey={SECTION.FEATURES_TRAITS}
                    PlaceholderComponent={SectionPlaceholder}
                    placeholderProps={{
                        name: 'Features & Traits',
                        IconComponent: isDarkMode ? LightFeaturesSvg : DarkFeaturesSvg,
                    }}
                    ContentComponent={FeaturesMobile}
                />
                <ComponentCarouselItem
                    itemKey={SECTION.PROFICIENCIES_LANGUAGES}
                    PlaceholderComponent={SectionPlaceholder}
                    placeholderProps={{
                        name: 'Proficiencies & Languages',
                        IconComponent: isDarkMode ? LightProficienciesSvg : DarkProficienciesSvg,
                    }}
                    ContentComponent={ProficiencyGroupsMobile}
                />
                <ComponentCarouselItem
                    itemKey={SECTION.DESCRIPTION}
                    PlaceholderComponent={SectionPlaceholder}
                    placeholderProps={{
                        name: 'Description',
                        IconComponent: isDarkMode ? LightDescriptionSvg : DarkDescriptionSvg,
                    }}
                    ContentComponent={DescriptionMobile}
                    isEnabled={!isReadonly}
                />
                <ComponentCarouselItem
                    itemKey={SECTION.NOTES}
                    PlaceholderComponent={SectionPlaceholder}
                    placeholderProps={{
                        name: 'Notes',
                        IconComponent: isDarkMode ? LightNotesSvg : DarkNotesSvg,
                    }}
                    ContentComponent={NotesMobile}
                    isEnabled={!isReadonly}
                />
                <ComponentCarouselItem
                    itemKey={SECTION.EXTRAS}
                    PlaceholderComponent={SectionPlaceholder}
                    placeholderProps={{
                        name: 'Extras',
                        IconComponent: isDarkMode ? LightExtrasSvg : DarkExtrasSvg,
                    }}
                    ContentComponent={ExtrasMobile}
                    isEnabled={!isReadonly}
                />
            </ComponentCarousel>
        );
    };

    renderQuickNav = (): React.ReactNode => {
        const { hasSpells, isReadonly, sidebarInfo } = this.props;

        return (
            <QuickNav
                onChange={this.handleComponentChange}
                onSidebarClick={this.handleSidebarClick}
                sidebarInfo={sidebarInfo}
            >
                <QuickNavItem id={SECTION.MAIN} cssKey="main" IconComponent={LightAbilitiesSvg} columns={2}>
                    Abilities, Saves, Senses
                </QuickNavItem>
                <QuickNavItem id={SECTION.SKILLS} cssKey="skills" IconComponent={LightSkillsSvg}>
                    Skills
                </QuickNavItem>
                <QuickNavItem id={SECTION.ACTIONS} cssKey="actions" IconComponent={LightActionsSvg}>
                    Actions
                </QuickNavItem>
                <QuickNavItem id={SECTION.EQUIPMENT} cssKey="equipment" IconComponent={LightEquipmentSvg}>
                    Inventory
                </QuickNavItem>
                <QuickNavItem id={SECTION.SPELLS} cssKey="spells" isEnabled={hasSpells} IconComponent={LightSpellsSvg}>
                    Spells
                </QuickNavItem>
                <QuickNavItem id={SECTION.FEATURES_TRAITS} cssKey="features" IconComponent={LightFeaturesSvg}>
                    Features &amp; Traits
                </QuickNavItem>
                <QuickNavItem
                    id={SECTION.PROFICIENCIES_LANGUAGES}
                    cssKey="proficiencies"
                    IconComponent={LightProficienciesSvg}
                >
                    Proficiencies &amp; Languages
                </QuickNavItem>
                <QuickNavItem
                    id={SECTION.DESCRIPTION}
                    cssKey="description"
                    isEnabled={!isReadonly}
                    IconComponent={LightDescriptionSvg}
                >
                    Description
                </QuickNavItem>
                <QuickNavItem id={SECTION.NOTES} cssKey="notes" isEnabled={!isReadonly} IconComponent={LightNotesSvg}>
                    Notes
                </QuickNavItem>
                <QuickNavItem
                    id={SECTION.EXTRAS}
                    cssKey="extras"
                    isEnabled={!isReadonly}
                    IconComponent={LightExtrasSvg}
                    columns={2}
                >
                    Extras
                </QuickNavItem>
            </QuickNav>
        );
    };

    renderSidebar = (): React.ReactNode => {
        const { swipedAmount } = this.state;
        const { sidebarPositioning } = this.props;
        return (
            <TransitionMotion
                styles={[
                    {
                        key: '1',
                        style: {
                            transform: spring(this.getPositionX(swipedAmount), { stiffness: 400, damping: 30 }),
                        },
                    },
                ]}
            >
                {(interpolatedStyles) => (
                    <React.Fragment>
                        {interpolatedStyles.map((config) => (
                            <Sidebar
                                key={config.key}
                                style={{
                                    ...sidebarPositioning,
                                    transform: `translateX(${config.style.transform}px)`,
                                }}
                                setSwipedAmount={(swipedAmount) => this.setState({ swipedAmount })}
                            />
                        ))}
                    </React.Fragment>
                )}
            </TransitionMotion>
        );
    };

    render() {
        const { decorationInfo } = this.props;

        return (
            <div
                className={`ct-character-sheet-mobile ${
                    DecorationUtils.isDarkMode(decorationInfo) ? 'ct-character-sheet-mobile--dark-mode' : ''
                }`}
            >
                <div className="ct-character-sheet-mobile__header">
                    <CharacterHeaderMobile />
                    <CombatMobile />
                </div>
                {this.renderSections()}
                {this.renderQuickNav()}
                {this.renderSidebar()}
                <ThemeStyles decorationInfo={decorationInfo} />
            </div>
        );
    }
}

function mapStateToProps(state: SheetAppState) {
    return {
        sidebarPositioning: sheetAppSelectors.getSidebarPositioning(state),
        sidebarInfo: sheetAppSelectors.getSidebarInfo(state),
        decorationInfo: rulesEngineSelectors.getDecorationInfo(state),
        hasSpells: rulesEngineSelectors.hasSpells(state),
        isReadonly: appEnvSelectors.getIsReadonly(state),
        envDimensions: appEnvSelectors.getDimensions(state),
    };
}

export default connect(mapStateToProps)(CharacterSheetMobile);
